import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/PrivacyPolicyModal.scss'; // Reusing the same styling

const TermsOfUseModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('terms');

  if (!isOpen) return null;

  return (
    <div className="privacy-policy-modal">
      <div className="privacy-policy-overlay" onClick={onClose}>
        <div className="privacy-policy-content" onClick={e => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>
            <span>&times;</span>
          </button>
          
          <div className="privacy-policy-header">
            <h1>{t('title')}</h1>
            <p className="date">{t('lastUpdated')}</p>
            <p className="date">{t('effectiveDate')}</p>
          </div>
          
          <div className="privacy-policy-body">
            {/* 1. Acceptance of Terms */}
            <section className="acceptance">
              <h2>{t('sections.acceptance.title')}</h2>
              <p>{t('sections.acceptance.content')}</p>
              <p>{t('sections.acceptance.agreement')}</p>
            </section>
            
            {/* 2. Description of Service */}
            <section className="description">
              <h2>{t('sections.description.title')}</h2>
              <p>{t('sections.description.content')}</p>
              
              <div className="subsection">
                <h3>{t('sections.description.features.title')}</h3>
                <ul>
                  {Array.isArray(t('sections.description.features.items', { returnObjects: true })) && 
                    t('sections.description.features.items', { returnObjects: true }).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))
                  }
                </ul>
              </div>
              
              <p>{t('sections.description.confirmation')}</p>
            </section>
            
            {/* 3. Eligibility */}
            <section className="eligibility">
              <h2>{t('sections.eligibility.title')}</h2>
              <p>{t('sections.eligibility.content')}</p>
              
              <div className="subsection">
                <h3>{t('sections.eligibility.warranties.title')}</h3>
                <ul>
                  {Array.isArray(t('sections.eligibility.warranties.items', { returnObjects: true })) && 
                    t('sections.eligibility.warranties.items', { returnObjects: true }).map((item, index) => (
                      <li key={index}>{item}</li>
                    ))
                  }
                </ul>
              </div>
              
              <p>{t('sections.eligibility.rights')}</p>
            </section>
            
            {/* 4. User Responsibilities and Code of Conduct */}
            <section className="responsibilities">
              <h2>{t('sections.responsibilities.title')}</h2>
              <p>{t('sections.responsibilities.content')}</p>
              
              {Array.isArray(t('sections.responsibilities.items', { returnObjects: true })) && 
                t('sections.responsibilities.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                    {item.subitems && (
                      <ul>
                        {Array.isArray(item.subitems) && 
                          item.subitems.map((subitem, subIndex) => (
                            <li key={subIndex}>{subitem}</li>
                          ))
                        }
                      </ul>
                    )}
                  </div>
                ))
              }
              
              <p>{t('sections.responsibilities.monitoring')}</p>
            </section>
            
            {/* 5. User Content and License */}
            <section className="user-content">
              <h2>{t('sections.userContent.title')}</h2>
              <p>{t('sections.userContent.content')}</p>
              <p>{t('sections.userContent.ownership')}</p>
              
              <ul>
                {Array.isArray(t('sections.userContent.license.items', { returnObjects: true })) && 
                  t('sections.userContent.license.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))
                }
              </ul>
              
              <p>{t('sections.userContent.future')}</p>
            </section>
            
            {/* 6. Subscription and Payment Terms */}
            <section className="subscription">
              <h2>{t('sections.subscription.title')}</h2>
              <p>{t('sections.subscription.content')}</p>
              
              {Array.isArray(t('sections.subscription.items', { returnObjects: true })) && 
                t('sections.subscription.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                    {item.subitems && (
                      <ul>
                        {Array.isArray(item.subitems) && 
                          item.subitems.map((subitem, subIndex) => (
                            <li key={subIndex}>{subitem}</li>
                          ))
                        }
                      </ul>
                    )}
                    {item.afterCancellation && <p>{item.afterCancellation}</p>}
                  </div>
                ))
              }
            </section>
            
            {/* 7. Intellectual Property Rights */}
            <section className="intellectual-property">
              <h2>{t('sections.intellectualProperty.title')}</h2>
              <p>{t('sections.intellectualProperty.content')}</p>
              
              {Array.isArray(t('sections.intellectualProperty.items', { returnObjects: true })) && 
                t('sections.intellectualProperty.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
            </section>
            
            {/* 8. Modifications, Suspension, and Termination of Service */}
            <section className="modifications">
              <h2>{t('sections.modifications.title')}</h2>
              <p>{t('sections.modifications.content')}</p>
              
              {Array.isArray(t('sections.modifications.items', { returnObjects: true })) && 
                t('sections.modifications.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
            </section>
            
            {/* 9. Disclaimers */}
            <section className="disclaimers">
              <h2>{t('sections.disclaimers.title')}</h2>
              <p>{t('sections.disclaimers.content')}</p>
              
              {Array.isArray(t('sections.disclaimers.items', { returnObjects: true })) && 
                t('sections.disclaimers.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                    {item.subitems && (
                      <ul>
                        {Array.isArray(item.subitems) && 
                          item.subitems.map((subitem, subIndex) => (
                            <li key={subIndex}>{subitem}</li>
                          ))
                        }
                      </ul>
                    )}
                  </div>
                ))
              }
            </section>
            
            {/* 10. Limitation of Liability */}
            <section className="liability">
              <h2>{t('sections.liability.title')}</h2>
              <p>{t('sections.liability.content')}</p>
              
              <ul>
                {Array.isArray(t('sections.liability.items', { returnObjects: true })) && 
                  t('sections.liability.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))
                }
              </ul>
              
              <p>{t('sections.liability.totalLiability')}</p>
            </section>
            
            {/* 11. Indemnification */}
            <section className="indemnification">
              <h2>{t('sections.indemnification.title')}</h2>
              <p>{t('sections.indemnification.content')}</p>
              
              <ul>
                {Array.isArray(t('sections.indemnification.items', { returnObjects: true })) && 
                  t('sections.indemnification.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))
                }
              </ul>
            </section>
            
            {/* 12. Governing Law and Dispute Resolution */}
            <section className="governing-law">
              <h2>{t('sections.governingLaw.title')}</h2>
              
              {Array.isArray(t('sections.governingLaw.items', { returnObjects: true })) && 
                t('sections.governingLaw.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
            </section>
            
            {/* 13. Miscellaneous */}
            <section className="miscellaneous">
              <h2>{t('sections.miscellaneous.title')}</h2>
              
              {Array.isArray(t('sections.miscellaneous.items', { returnObjects: true })) && 
                t('sections.miscellaneous.items', { returnObjects: true }).map((item, index) => (
                  <div className="subsection" key={index}>
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))
              }
              
              <p className="final-note">{t('sections.miscellaneous.final')}</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUseModal; 